/**
 * Editor bridge (contract §4): the SDK side of the visual editor opening
 * protocol. No backend session endpoint — trust is the panel window + an
 * origin allowlist + a nonce.
 *
 * The panel window is `window.opener` when the panel opened the page in a
 * new tab, or `window.parent` when the panel shows the page in an iframe
 * (its editor dialog). Either way the hello must come from that exact
 * window AND from an allowlisted origin, so a foreign site framing the
 * customer page cannot start the editor.
 *
 *   1. scale-up opens the customer page with `?b2m_ve=1` and polls
 *      `b2m-ve:hello` to the new window (targetOrigin = page origin).
 *   2. The SDK strips the param, enters editor mode (no experiment apply, no
 *      exposure, no anti-flicker) and waits for a hello whose
 *      `event.source` is the panel window and whose origin is allowlisted.
 *   3. The session is persisted in sessionStorage so in-site navigation
 *      keeps the editor; the editor bundle is lazy-loaded from the SDK's own
 *      directory (edge.b2metric.com fallback) and mounted.
 *   4. `hello.ui === 'remote'` (contract 10): the panel renders the editor UI
 *      itself and the bundle mounts a headless agent instead of the in-page
 *      side panel. `hello.ui === 'dock'` (new tab): the same agent plus the
 *      panel's UI docked into the page from `hello.dockUrl`. `ui` is kept in
 *      the session so a resume re-mounts the same kind of editor.
 */
import { getQueryParam, stripQueryParam } from './url-match';

export const SESSION_KEY = 'b2m_ve_session';
export const EDITOR_PARAM = 'b2m_ve';
export const EDITOR_BUNDLE = 'b2m-visual-editor.js';
export const DEFAULT_EDITOR_URL = 'https://edge.b2metric.com/b2m-visual-editor.js';
export const EDITOR_SCRIPT_ATTR = 'data-b2m-visual-editor';
export const MSG = {
  HELLO: 'b2m-ve:hello',
  READY: 'b2m-ve:ready',
  SAVE: 'b2m-ve:save',
  CLOSE: 'b2m-ve:close',
  RESUME: 'b2m-ve:resume'
};

/**
 * 'remote' (panel-rendered UI in the panel's frame, headless page agent),
 * 'dock' (new tab: the panel's UI docked into the page, dock-agent.js),
 * 'panel' (the in-page editor, also the dock's fallback) or undefined (= 'panel').
 */
export function editorUi(value) {
  return value === 'remote' || value === 'dock' || value === 'panel' ? value : undefined;
}

export function readEditorSession(win) {
  try {
    const v = win.sessionStorage.getItem(SESSION_KEY);
    const s = v ? JSON.parse(v) : null;
    return s && s.nonce && s.trustedOrigin ? s : null;
  } catch (e) {
    return null;
  }
}

export function writeEditorSession(win, session) {
  try {
    if (session) win.sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    else win.sessionStorage.removeItem(SESSION_KEY);
  } catch (e) {
    /* ignore */
  }
}

/**
 * `name` of the panel's editor-dialog iframe. Only a frame with this name
 * treats its parent as the panel: the panel's preview frames (and any other
 * site framing the page) never enter editor mode, even though they share the
 * dialog's sessionStorage partition and could see a leftover editor session.
 */
export const EDITOR_FRAME_NAME = 'b2m-visual-editor';

/**
 * The window that may drive the editor: the opener (panel opened this tab)
 * or, inside the panel's editor-dialog iframe, the parent. Null otherwise.
 */
export function getPanelWindow(win) {
  try { if (win.opener) return win.opener; } catch (e) { /* ignore */ }
  try {
    if (win.parent && win.parent !== win && win.name === EDITOR_FRAME_NAME) return win.parent;
  } catch (e) { /* ignore */ }
  return null;
}

/**
 * 'open'   — URL carries b2m_ve=1 and there is a panel window (param stripped)
 * 'resume' — a session exists in sessionStorage and the panel window is still there
 * null     — not editor mode (a bare param without a panel window is ignored as-is)
 */
export function detectEditorMode(win) {
  const panel = getPanelWindow(win);
  const param = getQueryParam(win.location.href, EDITOR_PARAM);
  if (param === '1') {
    if (!panel) return null;
    stripQueryParam(win, EDITOR_PARAM);
    return 'open';
  }
  if (panel && readEditorSession(win)) return 'resume';
  return null;
}

/** Editor bundle URL: next to the SDK bundle, else the static edge location. */
export function resolveEditorUrl(sdkScriptSrc, override) {
  if (override) return String(override);
  try {
    if (sdkScriptSrc) return new URL(EDITOR_BUNDLE, sdkScriptSrc).toString();
  } catch (e) {
    /* fall through */
  }
  return DEFAULT_EDITOR_URL;
}

export default class EditorBridge {
  /**
   * @param {{ win: Window, logger?: object, sdk?: object, sdkScriptSrc?: string, nonce?: string,
   *           getOrigins: () => string[], editorUrl?: string }} opts
   */
  constructor(opts) {
    this.win = opts.win;
    this.doc = this.win.document;
    this.logger = opts.logger || { log() {}, warn() {}, error() {} };
    this.sdk = opts.sdk || null;
    this.sdkScriptSrc = opts.sdkScriptSrc || '';
    this.nonce = opts.nonce || '';
    this.getOrigins = typeof opts.getOrigins === 'function' ? opts.getOrigins : () => [];
    this.editorUrl = opts.editorUrl || null;
    this.session = null;
    this.mode = null;
    this.loading = null;
    this.onMessage = this.onMessage.bind(this);
  }

  start(mode) {
    this.mode = mode;
    this.win.addEventListener('message', this.onMessage);
    if (mode === 'resume') {
      this.session = readEditorSession(this.win);
      if (this.session) {
        this.logger.log('[B2M Visual] resuming editor session');
        this._mount(true);
      }
    } else {
      this.logger.log('[B2M Visual] editor mode — waiting for the panel handshake');
    }
  }

  stop() {
    this.win.removeEventListener('message', this.onMessage);
  }

  isAllowedOrigin(origin) {
    const o = String(origin || '').toLowerCase().replace(/\/$/, '');
    return this.getOrigins().some(a => String(a).toLowerCase().replace(/\/$/, '') === o);
  }

  onMessage(event) {
    const data = event && event.data;
    if (!data || typeof data !== 'object' || data.type !== MSG.HELLO) return;
    const panel = getPanelWindow(this.win);
    if (!panel || event.source !== panel) {
      this.logger.warn('[B2M Visual] hello ignored — not from the panel window (opener / parent)');
      return;
    }
    if (!this.isAllowedOrigin(event.origin)) {
      this.logger.warn('[B2M Visual] hello ignored — origin not allowlisted:', event.origin);
      return;
    }
    if (!data.nonce || !data.experimentId) {
      this.logger.warn('[B2M Visual] hello ignored — missing nonce/experimentId');
      return;
    }
    const hello = {
      v: data.v || 1,
      nonce: String(data.nonce),
      experimentId: String(data.experimentId),
      experimentName: data.experimentName || '',
      groups: Array.isArray(data.groups) ? data.groups : [],
      changeset: data.changeset || null,
      editorUrl: data.editorUrl || this.win.location.href,
      ui: editorUi(data.ui)
    };
    if (data.focusGroupId) hello.focusGroupId = String(data.focusGroupId);
    if (hello.ui === 'dock') {
      // dock-agent.js only loads it from the trusted (panel) origin.
      hello.dockUrl = typeof data.dockUrl === 'string' ? data.dockUrl : '';
      hello.mode = data.mode === 'view' ? 'view' : 'edit';
      hello.group = data.group === 'control' ? 'control' : 'test';
      hello.readOnly = data.readOnly === true;
    }
    this.session = { nonce: hello.nonce, trustedOrigin: event.origin, hello, ui: hello.ui };
    writeEditorSession(this.win, this.session);
    this._mount(false);
  }

  _mount(resume) {
    const win = this.win;
    const session = this.session;
    const mountNow = () => {
      const api = win.B2MVisualEditor;
      if (!api || typeof api.mount !== 'function') {
        this.logger.error('[B2M Visual] editor bundle loaded but B2MVisualEditor.mount is missing');
        return;
      }
      try {
        api.mount({
          hello: session.hello,
          trustedOrigin: session.trustedOrigin,
          nonce: session.nonce,
          sdk: this.sdk,
          resume: !!resume,
          ui: editorUi(session.ui) || editorUi(session.hello && session.hello.ui)
        });
      } catch (e) {
        this.logger.error('[B2M Visual] editor mount failed:', e);
      }
    };
    if (win.B2MVisualEditor && typeof win.B2MVisualEditor.mount === 'function') {
      mountNow();
      return;
    }
    this._loadEditor().then(mountNow).catch(e => this.logger.error('[B2M Visual] editor load failed:', e));
  }

  _loadEditor() {
    if (this.loading) return this.loading;
    const doc = this.doc;
    const src = resolveEditorUrl(this.sdkScriptSrc, this.editorUrl);
    this.loading = new Promise((resolve, reject) => {
      const existing = doc.querySelector(`script[${EDITOR_SCRIPT_ATTR}]`);
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject(new Error(`Failed to load: ${src}`)));
        if (this.win.B2MVisualEditor) resolve();
        return;
      }
      const script = doc.createElement('script');
      script.src = src;
      script.async = true;
      script.setAttribute(EDITOR_SCRIPT_ATTR, 'true');
      if (this.nonce) script.setAttribute('nonce', this.nonce);
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load: ${src}`));
      (doc.head || doc.documentElement).appendChild(script);
      this.logger.log(`[B2M Visual] editor loading from ${src}`);
    });
    return this.loading;
  }
}
